import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { useInitiateOauthTrigger } from 'hooks/useInitiateOauthTrigger/useInitiateOauthTrigger'
import { validateScheduledPost } from 'utils/validateScheduledPost'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import { useCreateScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useCreateScheduledPostTarget'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useAccountLimitStatus } from 'hooks/useAccountLimitStatus'
import { useQueryClient } from 'react-query'
import Styles from './IntegrationSelector.module.scss'
import { PostedIntegrationRow } from './PostedIntegrationRow'
import { AddNewAccountRow } from './AddNewAccountRow'

interface PostedIntegrationListProps {
  integrations: ExternalIntegration[]
  platform: ExternalIntegrationEnum
  targets: ScheduledPostTarget[]
  scheduledPost: ScheduledPostShow | null
  accountIdsNeedReauth: number[]
  onAddNewAccount: (popUp?: Window) => void
}

export const PostedIntegrationList: React.FC<PostedIntegrationListProps> = ({
  integrations,
  platform,
  targets,
  scheduledPost,
  accountIdsNeedReauth,
  onAddNewAccount,
}) => {
  const { handleConnect } = useInitiateOauthTrigger({
    provider: platform,
    popUp: true,
    onInitiatedOauth: onAddNewAccount,
  })
  const openWindowSafely = useOpenWindowSafely()
  const { mutate: publishTargets } = usePublishScheduledPostTargets()
  const { mutateAsync: createTarget } = useCreateScheduledPostTarget()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { connect: connectAccountPopup } = useConnectAccountPopup()
  const { isAtOrAboveMaxAccounts } = useAccountLimitStatus()

  const handlePublish = (targetId: number) => {
    if (!scheduledPost?.id) return
    publishTargets({ scheduledPostId: scheduledPost.id, targetIds: [targetId] })
  }

  const handleCreateAndPublish = async (integration: ExternalIntegration) => {
    if (!scheduledPost?.id) return
    const created = await createTarget({
      scheduled_post_id: scheduledPost.id,
      provider: platform,
      external_integration_id: integration.id,
      integration_name: integration.name,
    })
    const firstTarget = Array.isArray(created) ? created[0] : created
    if (firstTarget?.id) {
      publishTargets({ scheduledPostId: scheduledPost.id, targetIds: [firstTarget.id] })
    }
  }

  const handleReauth = (popup?: Window) => {
    connectAccountPopup(popup, {
      onConnect: () => {
        queryClient.invalidateQueries(calendarKeys.all)
        queryClient.invalidateQueries(['externalIntegrations', workspaceId])
      },
    })
  }

  return (
    <div className={Styles.postedList} role="menu" aria-label="Posted accounts">
      {integrations.map((integration) => {
        const target = targets.find((t) => t.provider === platform && t.external_integration_id === integration.id)
        const needsReauth = accountIdsNeedReauth.includes(integration.id)
        const validation = validateScheduledPost(scheduledPost, platform, target)

        return (
          <PostedIntegrationRow
            key={integration.id}
            integration={integration}
            target={target}
            platform={platform}
            needsReauth={needsReauth}
            validation={validation}
            onPublish={handlePublish}
            onPublishWithoutTarget={() => handleCreateAndPublish(integration)}
            onViewPost={openWindowSafely}
            onReauth={handleReauth}
          />
        )
      })}
      <div className={Styles.postedSeparator} />
      <AddNewAccountRow
        showPaywallHint={isAtOrAboveMaxAccounts}
        onClick={() => handleConnect()}
        testId="connect-new-account"
      />
    </div>
  )
}
